import React from 'react';
import c from 'classnames';

import { SearchkitWrapper } from '@uncinc/react-searchkit';
import { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

import { APP_OPTIONS, SEARCHKIT_HOST_URL } from '../../config/constants';

import './style.scss';

// Subcomponents.
import ActiveFacets from './ActiveFacets';
import AutosuggestHit from './AutosuggestHit';
import Facets from './Facets';
import Results from './Results';
import SearchBar from './SearchBar';
import SearchBarAutosuggest from './SearchBarAutosuggest';
import Stats from './Stats';

interface SearchAllContentSubComponents {
  ActiveFacets: typeof ActiveFacets;
  AutosuggestHit: typeof AutosuggestHit;
  Facets: typeof Facets;
  Results: typeof Results;
  SearchBar: typeof SearchBar;
  SearchBarAutosuggest: typeof SearchBarAutosuggest;
  Stats: typeof Stats;
}

export interface SearchAllContentProps extends Omit<SearchkitWrapperProps, 'connection' | 'searchSettings' | 'indexName'> {
  className?: string;
}

// eslint-disable-next-line max-len
const SearchAllContent: React.FC<SearchAllContentProps> & SearchAllContentSubComponents = (props) => {
  const {
    className,
    routing,
    children,
    ...restProps
  } = props;

  const classNames = c('SearchAllContent', className);

  const searchAttributes = [
    'target_audience',
    'theme',
    'page_title',
    'name',
    'description',
    'body',
    'body_summarized',
    'author_full_name',
    'contact_person_full_name',
    'rendered_item',
    'colleague_full_name',
  ];

  const resultAttributes = [
    'uuid',
    'type',
    'vid',
    'url',
    'page_title',
    'name',
    'description',
    'body',
    'body_summarized',
    'image_style_uri',
    'theme_path',
    'author_full_name',
    'author_profile_picture',
    'contact_person_full_name',
    'contact_person_profile_picture',
    'image_svg_url',
    'colleague_full_name',
    'colleague_profile_picture',
    'colleague_team_url',
    'colleague_team_title',
    'colleague_team_summary',
    'parent_uuid',
  ];

  return (
    <SearchkitWrapper
      {...restProps}
      className={classNames}
      connection={{
        host: SEARCHKIT_HOST_URL,
      }}
      routing={routing}
      indexName={APP_OPTIONS.parameters.searchkitIndexAllContent}
      searchSettings={{
        search_attributes: searchAttributes,
        result_attributes: resultAttributes,
        facet_attributes: [
          {
            attribute: 'targetAudience',
            field: 'target_audience',
            type: 'string',
            facetQuery: (
              field,
              size,
            ) => ({
              terms: {
                field,
                size,
                min_doc_count: 0,
              },
            }),
          },
          {
            attribute: 'theme',
            field: 'theme',
            type: 'string',
            facetQuery: (
              field,
              size,
            ) => ({
              terms: {
                field,
                size,
                min_doc_count: 0,
              },
            }),
          },
          {
            attribute: 'type',
            field: 'type',
            type: 'string',
            facetQuery: (
              field,
              size,
            ) => ({
              terms: {
                field,
                size,
                min_doc_count: 0,
              },
            }),
          },
        ],
      }}
    >
      {children}
    </SearchkitWrapper>
  );
};

SearchAllContent.ActiveFacets = ActiveFacets;
SearchAllContent.AutosuggestHit = AutosuggestHit;
SearchAllContent.Facets = Facets;
SearchAllContent.Results = Results;
SearchAllContent.SearchBar = SearchBar;
SearchAllContent.SearchBarAutosuggest = SearchBarAutosuggest;
SearchAllContent.Stats = Stats;

export default SearchAllContent;
